// Use standalone version for Electron compatibility

// Global registry of all active TradingChart instances
// This allows updating ALL instances when price changes, ensuring the visible one always updates
if (!window._tradingChartInstances) {
    window._tradingChartInstances = new Set();
}

export class TradingChart {
    constructor(container, selectedAsset = 'ETH', interval = '15m') {
        // Unique instance ID for debugging
        this._instanceId = Math.random().toString(36).substr(2, 9);
        // console.log('[CHART-INSTANCE] 🆕 NEW TradingChart created, instanceId:', this._instanceId, 'asset:', selectedAsset);
        
        // Register this instance globally
        window._tradingChartInstances.add(this);
        
        this.container = container;
        this.candlestickSeries = null;
        this.volumeSeries = null; // Volume histogram series
        this.selectedAsset = selectedAsset;
        this.interval = interval;
        this.subscription = null;
        
        // Store last positions/orders for recreation after setData() calls
        this.lastPositions = null;
        this.lastOrders = null;
        
        // Track if initial data was loaded successfully
        this.hasInitialData = false;
        
        // Real-time price line tracking
        this.currentPriceLine = null;
        this.lastPrice = 0;
        
        // Track current candle for live price sync with candle bar
        this.lastFormattedCandle = null;
        
        // Cache volume data separately (candlestickSeries.data() doesn't preserve volume)
        this.volumeDataCache = new Map(); // Key: timestamp, Value: { value, color }
        
        // Spot pair display names mapping - dynamically loaded from spotMeta
        this.spotPairs = {};
        this.spotPairsLoaded = false;
        
        // Extended historical data loading tracking
        this._extendedLoadId = 0;
        this._isLoadingExtended = false;
        
        // Chart display preferences
        this.showCrosshair = localStorage.getItem('chart_show_crosshair') !== 'false'; // Default true
        this.showVolume = localStorage.getItem('chart_show_volume') !== 'false'; // Default true

        this.init();
    }

    /**
     * Initialize the chart component
     */
    init() {
        
        if (!this.container) {
            // console.error('❌ Chart container not provided');
            return;
        }

        // Wait for container to be in DOM
        if (!document.contains(this.container)) {
            setTimeout(() => this.init(), 100);
            return;
        }

        // Load spot pairs mapping in background
        this.loadSpotPairs();
        
        // Show loading state
        this.showLoadingState();
        
        // Start immediately - no artificial delays
        (async () => {
            try {
                await this.createChart();
                
                // Try to get price from terminal view immediately
                if (window.currentTerminalView?.lastKnownPrice && !this.lastPrice) {
                    this.lastPrice = window.currentTerminalView.lastKnownPrice;
                }
                
                // Load data with timeout protection - don't block indefinitely
                await this.loadInitialData();
                
                // Hide loading state immediately when data ready
                this.hideLoadingState();
            } catch (error) {
                // console.error('Chart initialization error:', error);
                this.hideLoadingState();
            }
        })();
    }

    /**
     * Create the chart instance with Hyperliquid theme
     */
    createChart() {
        
        if (typeof window.LightweightCharts === 'undefined') {
            // console.error('LightweightCharts not loaded');
            return;
        }
        
        this.chart = window.LightweightCharts.createChart(this.container, {
            width: this.container.clientWidth,
            height: this.container.clientHeight,
            layout: {
                background: { color: '#08090A' },
                textColor: '#FFFFFD',
            },
            grid: {
                vertLines: { color: '#313244' },
                horzLines: { color: '#313244' },
            },
            crosshair: this.showCrosshair ? {
                mode: 1, // Normal crosshair mode
                vertLine: {
                    width: 1,
                    color: '#758696',
                    style: 2, // Dashed line
                    labelBackgroundColor: '#00BF63',
                },
                horzLine: {
                    width: 1,
                    color: '#758696',
                    style: 2,
                    labelBackgroundColor: '#00BF63',
                },
            } : {
                mode: 0,
                vertLine: {
                    visible: false,
                    labelVisible: false,
                },
                horzLine: {
                    visible: false,
                    labelVisible: false,
                },
            },
            rightPriceScale: {
                borderColor: '#45475a',
                textColor: '#cdd6f4',
            },
            timeScale: {
                borderColor: '#45475a',
                textColor: '#cdd6f4',
                timeVisible: true,
                secondsVisible: false,
                rightOffset: 12,
                barSpacing: 6,
                fixLeftEdge: false,
                lockVisibleTimeRangeOnResize: true,
            },
            handleScale: {
                axisPressedMouseMove: {
                    time: true,
                    price: true,  // Enable price axis dragging
                },
            },
            handleScroll: {
                mouseWheel: true,
                pressedMouseMove: true,
                horzTouchDrag: true,
                vertTouchDrag: true,
            },
        });

        // Ensure chart has proper dimensions (critical for crosshair accuracy)
        this.chart.resize(this.container.clientWidth, this.container.clientHeight);

        // Create candlestick series
        this.candlestickSeries = this.chart.addCandlestickSeries({
            upColor: '#a6e3a1',
            downColor: '#f38ba8',
            borderDownColor: '#f38ba8',
            borderUpColor: '#a6e3a1',
            wickDownColor: '#f38ba8',
            wickUpColor: '#a6e3a1',
            lastValueVisible: false,  // Hide series' default last value label
            priceLineVisible: false,  // Hide series' default price line
            priceFormat: {
                type: 'custom',
                minMove: 0.000001,
                formatter: (price) => {
                    if (!price || price <= 0) return '0';
                    
                    // Use 5 significant digits (matches orderbook precision)
                    return this.formatToSignificantDigits(price, 5);
                }
            },
        });
        
        // Adjust candlestick margins to make room for volume
        this.candlestickSeries.priceScale().applyOptions({
            scaleMargins: {
                top: 0.1,
                bottom: this.showVolume ? 0.3 : 0.1, // 30% reserved for volume if enabled
            },
        });
        
        // Create volume histogram series if enabled
        if (this.showVolume) {
            this.volumeSeries = this.chart.addHistogramSeries({
                priceFormat: { type: 'volume' },
                priceScaleId: '', // Overlay (no separate scale)
            });
            
            // Apply scaleMargins to priceScale (correct pattern)
            this.volumeSeries.priceScale().applyOptions({
                scaleMargins: {
                    top: 0.8,     // 20% height (reduced from 30%)
                    bottom: 0,    // Anchor to bottom edge
                },
            });
        }

        // Candlestick series created
        
        // Apply any pending price update that arrived before chart was ready
        if (this.lastPrice > 0) {
            this.updateCurrentPrice(this.lastPrice);
        }

        // Track position price lines
        this.positionLines = new Map();
        
        // Hide chart widget until data loads
        const chartWidget = this.container.querySelector('.tv-lightweight-charts');
        if (chartWidget) {
            chartWidget.style.visibility = 'hidden';
        }
        
        // BULLETPROOF FIX: Aggressive periodic check to ensure price line exists
        // This handles race conditions where setData() clears the line
        this.priceLineCheckInterval = setInterval(() => {
            if (this.lastPrice > 0 && this.candlestickSeries && !this.currentPriceLine) {
                // console.log('[CHART] 🔄 Price line missing, recreating at:', this.lastPrice);
                this.updateCurrentPrice(this.lastPrice);
            }
        }, 300); // Check every 300ms for faster recovery

        // Handle resize - store bound function for proper cleanup
        this._boundHandleResize = this.handleResize.bind(this);
        window.addEventListener('resize', this._boundHandleResize);
        
    }

    /**
     * Load initial historical data using Charts View advanced data loading
     * REPLACED: Now uses ChartDataManager approach with better caching, validation, and error handling
     */
    async loadInitialData() {
        try {
            if (!this.candlestickSeries) {
                // console.error('❌ Candlestick series not available, cannot load data');
                return;
            }
            
            // Check if browser or Electron mode
            const isElectron = window.process?.versions?.electron;
            
            // OPTIMIZATION: Use pre-warmed data if available (browser mode only)
            if (!isElectron && window.BrowserCandleTracker?.candleData?.length > 0) {
                const tracker = window.BrowserCandleTracker;
                if (tracker.currentAsset === this.selectedAsset && tracker.interval === this.interval) {
                    // Use pre-warmed data instead of fetching
                    const formattedData = this.formatCandleData(tracker.candleData);
                    this.candlestickSeries.setData(formattedData);
                    
                    // Show chart widget now that data is loaded
                    const chartWidget = this.container.querySelector('.tv-lightweight-charts');
                    if (chartWidget) {
                        chartWidget.style.visibility = 'visible';
                    }
                    
                    // Always populate volume cache (even if series doesn't exist yet)
                    if (formattedData.length > 0) {
                        const volumeData = this.extractVolumeData(formattedData);
                        
                        // Populate volume cache
                        volumeData.forEach(vol => {
                            this.volumeDataCache.set(vol.time, { value: vol.value, color: vol.color });
                        });
                        
                        // Set volume data only if series exists
                        if (this.volumeSeries) {
                            this.volumeSeries.setData(volumeData);
                        }
                    }
                    
                    this.hasInitialData = true;
                    
                    // Set visible range to show ~100 recent candles (match API path behavior)
                    if (formattedData.length > 100) {
                        const endTime = formattedData[formattedData.length - 1].time;
                        const startTime = formattedData[formattedData.length - 100].time;
                        this.chart.timeScale().setVisibleRange({ from: startTime, to: endTime });
                    } else {
                        this.fitContent();
                    }
                    
                    // Apply price line if available
                    if (this.lastPrice > 0) {
                        this.updateCurrentPrice(this.lastPrice);
                    } else if (window.currentTerminalView?.lastKnownPrice) {
                        this.lastPrice = window.currentTerminalView.lastKnownPrice;
                        this.updateCurrentPrice(this.lastPrice);
                    }
                    
                    // Launch Phase 2: Extended historical data loading in background (non-blocking)
                    this.loadExtendedHistoricalData().catch(err => {
                        // Silent fallback - chart continues working with tracker snapshot
                        console.error('[CHART] Extended historical load failed:', err);
                    });
                    
                    return; // Skip HTTP fetch - data already loaded, Phase 2 launched
                }
            }
            
            // Calculate time range based on interval for appropriate historical coverage
            const now = Date.now();
            let historicalDays;
            switch (this.interval) {
                case '1m':
                    historicalDays = 1; // 1 day
                    break;
                case '3m':
                    historicalDays = 2; // 2 days
                    break;
                case '5m':
                    historicalDays = 7; // 1 week
                    break;
                case '15m':
                    historicalDays = 14; // 2 weeks for better chart context
                    break;
                case '30m':
                    historicalDays = 30; // 1 month for better chart context
                    break;
                case '1h':
                    historicalDays = 60; // 2 months
                    break;
                case '2h':
                    historicalDays = 90; // 3 months
                    break;
                case '4h':
                    historicalDays = 180; // 6 months
                    break;
                case '8h':
                    historicalDays = 270; // 9 months
                    break;
                case '12h':
                    historicalDays = 365; // 1 year
                    break;
                case '1d':
                    historicalDays = 365; // 1 year
                    break;
                case '3d':
                    historicalDays = 730; // 2 years
                    break;
                case '1w':
                    historicalDays = 1095; // 3 years
                    break;
                case '1M':
                    historicalDays = 1825; // 5 years
                    break;
                default:
                    historicalDays = 30; // Default 30 days
            }
            
            const startTime = now - (historicalDays * 24 * 60 * 60 * 1000);
            const endTime = now;
            
            // console.log(`📊 Loading ${historicalDays} days of ${this.selectedAsset} ${this.interval} data...`);
            
            // Request data from Hyperliquid API with detailed params logging
            const requestParams = {
                asset: this.selectedAsset,
                interval: this.interval,
                startTime: startTime,
                endTime: endTime,
                limit: 5000
            };
            
            let response;
            
            if (!isElectron) {
                // Browser mode - use BrowserHyperliquidClient
                if (!window.BrowserHyperliquidClient?.isInitialized) {
                    // console.error('❌ BrowserHyperliquidClient not initialized');
                    return;
                }
                
                const candleData = await window.BrowserHyperliquidClient.getCandleData(
                    this.selectedAsset,
                    this.interval,
                    startTime,
                    endTime
                );
                response = { success: true, data: candleData };
            } else {
                // Electron mode - use IPC
                response = await window.electronAPI.invoke('hyperliquid:getCandleData', requestParams);
            }
            
            if (response.success && Array.isArray(response.data)) {
                // Validate and clean the data before setting (Charts View pattern)
                const validData = response.data.filter(candle => {
                    if (!candle || typeof candle !== 'object') return false;
                    
                    const time = candle.t || candle.time;
                    const open = parseFloat(candle.o || candle.open);
                    const high = parseFloat(candle.h || candle.high);
                    const low = parseFloat(candle.l || candle.low);
                    const close = parseFloat(candle.c || candle.close);
                    const volume = parseFloat(candle.v || candle.volume || 0);
                    
                    return time && !isNaN(open) && !isNaN(high) && !isNaN(low) && !isNaN(close) && volume >= 0;
                });
                
                // console.log(`📊 Loaded ${validData.length}/${response.data.length} valid candles for ${this.selectedAsset} ${this.interval}`);
                
                // Format and set data
                const formattedData = this.formatCandleData(validData);
                // console.log('[CHART-DATA] setData called on instance:', this._instanceId, 'currentPriceLine before:', !!this.currentPriceLine);
                this.candlestickSeries.setData(formattedData);
                // console.log('[CHART-DATA] setData complete on instance:', this._instanceId, 'currentPriceLine after:', !!this.currentPriceLine);
                
                // Show chart widget now that data is loaded
                const chartWidget = this.container.querySelector('.tv-lightweight-charts');
                if (chartWidget) {
                    chartWidget.style.visibility = 'visible';
                }
                
                // Always populate volume cache (even if series doesn't exist yet)
                if (formattedData.length > 0) {
                    const volumeData = this.extractVolumeData(formattedData);
                    
                    // Populate volume cache
                    volumeData.forEach(vol => {
                        this.volumeDataCache.set(vol.time, { value: vol.value, color: vol.color });
                    });
                    
                    // Set volume data only if series exists
                    if (this.volumeSeries) {
                        this.volumeSeries.setData(volumeData);
                    }
                }
                
                // Mark that we have initial data
                this.hasInitialData = true;
                
                // Set visible range to show ~100 recent candles
                if (formattedData.length > 100) {
                    const endTime = formattedData[formattedData.length - 1].time;
                    const startTime = formattedData[formattedData.length - 100].time;
                    this.chart.timeScale().setVisibleRange({ from: startTime, to: endTime });
                } else {
                    this.fitContent();
                }
                
                // CRITICAL: Recreate price line immediately after setData() clears it
                // console.log('[CHART-DATA] About to recreate price line, lastPrice:', this.lastPrice, 'terminalLastKnownPrice:', window.currentTerminalView?.lastKnownPrice);
                if (this.lastPrice > 0) {
                    this.updateCurrentPrice(this.lastPrice);
                } else if (window.currentTerminalView?.lastKnownPrice) {
                    // Fallback: Use terminal view's last known price if chart hasn't received price yet
                    this.lastPrice = window.currentTerminalView.lastKnownPrice;
                    this.updateCurrentPrice(this.lastPrice);
                }
                
                // BULLETPROOF: Multiple aggressive retries to ensure price line appears
                const retryDelays = [50, 150, 300, 600, 1000];
                retryDelays.forEach(delay => {
                    setTimeout(() => {
                        if (this.candlestickSeries && !this.currentPriceLine) {
                            // Try to get price from multiple sources
                            const price = this.lastPrice || window.currentTerminalView?.lastKnownPrice;
                            if (price > 0) {
                                this.lastPrice = price;
                                this.updateCurrentPrice(price);
                            }
                        }
                    }, delay);
                });
            } else {
                // console.error(`❌ Failed to load initial chart data:`, {
                //     success: response.success,
                //     error: response.error,
                //     hasData: Array.isArray(response.data),
                //     dataLength: response.data?.length
                // });
            }
        } catch (error) {
            // console.error('❌ Error loading initial chart data:', error);
        }
    }

    /**
     * Load extended historical data in background after initial display
     * Called automatically after quick initial load completes (Phase 2)
     * Loads weeks/months of historical data based on interval
     */
    async loadExtendedHistoricalData() {
        // Guard: Only load if we have initial data and chart is ready
        if (!this.hasInitialData || !this.candlestickSeries) {
            return;
        }
        
        // Stale request detection: increment load ID
        const loadId = ++this._extendedLoadId;
        this._isLoadingExtended = true;
        
        try {
            const now = Date.now();
            
            // Calculate FULL historical range based on interval (Phase 2 extended load)
            let totalHistoricalDays;
            switch (this.interval) {
                case '1m':
                    totalHistoricalDays = 7;    // 1 week
                    break;
                case '3m':
                    totalHistoricalDays = 14;   // 2 weeks
                    break;
                case '5m':
                    totalHistoricalDays = 28;   // 4 weeks
                    break;
                case '15m':
                    totalHistoricalDays = 60;   // 2 months for extended scroll
                    break;
                case '30m':
                    totalHistoricalDays = 90;   // 3 months for extended scroll
                    break;
                case '1h':
                    totalHistoricalDays = 180;  // 6 months
                    break;
                case '2h':
                    totalHistoricalDays = 270;  // 9 months
                    break;
                case '4h':
                    totalHistoricalDays = 365;  // 1 year
                    break;
                case '8h':
                    totalHistoricalDays = 540;  // 18 months
                    break;
                case '12h':
                    totalHistoricalDays = 730;  // 2 years
                    break;
                case '1d':
                    totalHistoricalDays = 1095; // 3 years
                    break;
                case '3d':
                    totalHistoricalDays = 1825; // 5 years
                    break;
                case '1w':
                    totalHistoricalDays = 3650; // 10 years
                    break;
                case '1M':
                    totalHistoricalDays = 3650; // 10 years
                    break;
                default:
                    totalHistoricalDays = 30;   // Default 30 days
            }
            
            const startTime = now - (totalHistoricalDays * 24 * 60 * 60 * 1000);
            const endTime = now;
            
            // Optional: Show subtle loading indicator
            this.showExtendedLoadingBadge(`Loading ${totalHistoricalDays} days...`);
            
            // Fetch extended data
            const isElectron = window.process?.versions?.electron;
            let candles;
            
            if (!isElectron) {
                // Browser mode - use BrowserHyperliquidClient
                if (!window.BrowserHyperliquidClient?.isInitialized) {
                    return;
                }
                candles = await window.BrowserHyperliquidClient.getCandleData(
                    this.selectedAsset,
                    this.interval,
                    startTime,
                    endTime
                );
            } else {
                // Electron mode - use IPC
                const response = await window.electronAPI.invoke('hyperliquid:getCandleData', {
                    asset: this.selectedAsset,
                    interval: this.interval,
                    startTime,
                    endTime
                });
                candles = response?.data;
            }
            
            // Check if request is stale (user switched interval/asset during load)
            if (loadId !== this._extendedLoadId) {
                return; // Stale request, abort
            }
            
            if (!candles || !Array.isArray(candles) || candles.length === 0) {
                return;
            }
            
            // Validate and format extended data
            const validCandles = candles.filter(candle => {
                if (!candle || typeof candle !== 'object') return false;
                const time = candle.t;
                const open = parseFloat(candle.o);
                const high = parseFloat(candle.h);
                const low = parseFloat(candle.l);
                const close = parseFloat(candle.c);
                return time && !isNaN(open) && !isNaN(high) && !isNaN(low) && !isNaN(close);
            });
            
            const formattedExtended = this.formatCandleData(validCandles);
            
            if (formattedExtended.length === 0) {
                return;
            }
            
            // Get current chart data (OHLC only)
            const currentData = this.getData();
            
            // Merge with deduplication by timestamp
            const mergedData = this.mergeHistoricalData(currentData, formattedExtended);
            
            // Final stale check before updating chart
            if (loadId !== this._extendedLoadId) {
                return; // Stale request, abort
            }
            
            // Update chart with full merged data
            this.candlestickSeries.setData(mergedData);
            
            // Always populate volume cache from extended data (even if series doesn't exist yet)
            if (mergedData.length > 0) {
                // Extract volume from extended data and update cache
                const newVolumeData = this.extractVolumeData(formattedExtended);
                newVolumeData.forEach(vol => {
                    this.volumeDataCache.set(vol.time, { value: vol.value, color: vol.color });
                });
                
                // Update volume series only if it exists
                if (this.volumeSeries) {
                    // Rebuild volume series from complete cache
                    const volumeData = Array.from(this.volumeDataCache.entries())
                        .map(([time, vol]) => ({ time, value: vol.value, color: vol.color }))
                        .sort((a, b) => a.time - b.time);
                    
                    this.volumeSeries.setData(volumeData);
                }
            }
            
            // Restore all state after setData (overlays, price lines)
            this.recreateStateAfterSetData();
            
            // Set visible range to show ~100 recent candles (like Hyperliquid UI)
            // Don't zoom out to show ALL data - user can manually zoom if needed
            if (mergedData.length > 100) {
                const endTime = mergedData[mergedData.length - 1].time;
                const startTime = mergedData[mergedData.length - 100].time;
                this.chart.timeScale().setVisibleRange({ from: startTime, to: endTime });
            } else {
                // Fallback for small datasets
                this.fitContent();
            }
            
        } catch (error) {
            console.error('[CHART] Extended historical load error:', error);
        } finally {
            this._isLoadingExtended = false;
            this.hideExtendedLoadingBadge();
        }
    }


    /**
     * Format number to significant digits (for price precision)
     * Matches orderbook precision handling
     */
    formatToSignificantDigits(num, sigDigits = 5) {
        if (num === 0 || !num) return '0';
        
        const absNum = Math.abs(num);
        
        // BTC exception: if price >= 10000, show whole numbers only (no decimals)
        if (absNum >= 10000) {
            return Math.round(num).toString();
        }
        
        // For other assets: use significant digits
        const magnitude = Math.floor(Math.log10(absNum));
        const precision = sigDigits - magnitude - 1;
        
        if (precision < 0) {
            // Instead of scientific notation, show with 2 decimals
            return num.toFixed(2);
        } else {
            return num.toFixed(Math.max(0, precision));
        }
    }

    /**
     * Format candle data for Lightweight Charts
     */
    formatCandleData(candles) {
        if (!Array.isArray(candles)) return [];

        return candles.map(candle => this.formatSingleCandle(candle)).filter(Boolean);
    }

    /**
     * Format single candle data
     */
    formatSingleCandle(candle) {
        if (!candle || typeof candle !== 'object') {
            return null;
        }

        // Hyperliquid SDK format: { t, T, s, i, o, c, h, l, v, n }
        const time = candle.t; // Opening timestamp in ms
        const open = parseFloat(candle.o);
        const high = parseFloat(candle.h);
        const low = parseFloat(candle.l);
        const close = parseFloat(candle.c);
        const volume = parseFloat(candle.v || candle.volume || 0);

        if (!time || isNaN(open) || isNaN(high) || isNaN(low) || isNaN(close)) {
            return null;
        }

        return {
            time: Math.floor(time / 1000), // Convert ms to seconds for lightweight-charts
            open,
            high,
            low,
            close,
            volume: volume >= 0 ? volume : 0
        };
    }

    /**
     * Extract volume data from formatted candle data
     * @param {Array} candleData - Formatted candle data with volume
     * @returns {Array} Volume histogram data with colors
     */
    extractVolumeData(candleData) {
        if (!Array.isArray(candleData)) return [];
        
        return candleData.map(candle => {
            if (!candle || !candle.volume || candle.volume <= 0) return null;
            
            // Color based on candle direction
            const isGreen = candle.close >= candle.open;
            const color = isGreen ? 
                'rgba(166, 227, 161, 0.4)' :  // Green with 40% opacity
                'rgba(243, 139, 168, 0.4)';   // Red with 40% opacity
            
            return {
                time: candle.time,
                value: candle.volume,
                color: color
            };
        }).filter(Boolean);
    }

    /**
     * Toggle crosshair visibility
     * @param {boolean} show - Whether to show crosshair
     */
    toggleCrosshair(show) {
        if (!this.chart) return;
        
        this.showCrosshair = show;
        localStorage.setItem('chart_show_crosshair', show);
        
        this.chart.applyOptions({
            crosshair: show ? {
                mode: 1,
                vertLine: {
                    visible: true,
                    labelVisible: true,
                    width: 1,
                    color: '#758696',
                    style: 2,
                    labelBackgroundColor: '#00BF63',
                },
                horzLine: {
                    visible: true,
                    labelVisible: true,
                    width: 1,
                    color: '#758696',
                    style: 2,
                    labelBackgroundColor: '#00BF63',
                },
            } : {
                mode: 0,
                vertLine: { visible: false, labelVisible: false },
                horzLine: { visible: false, labelVisible: false },
            }
        });
    }

    /**
     * Toggle volume histogram visibility
     * @param {boolean} show - Whether to show volume
     */
    toggleVolume(show) {
        if (!this.chart) return;
        
        this.showVolume = show;
        localStorage.setItem('chart_show_volume', show);
        
        if (show && !this.volumeSeries) {
            // Create volume series
            this.volumeSeries = this.chart.addHistogramSeries({
                priceFormat: { type: 'volume' },
                priceScaleId: '',
            });
            
            // Apply scaleMargins to priceScale (correct pattern)
            this.volumeSeries.priceScale().applyOptions({
                scaleMargins: {
                    top: 0.8,     // 20% height (reduced from 30%)
                    bottom: 0,    // Anchor to bottom edge
                },
            });
            
            // Populate with cached volume data
            if (this.volumeDataCache.size > 0) {
                const volumeData = Array.from(this.volumeDataCache.entries())
                    .map(([time, vol]) => ({ time, value: vol.value, color: vol.color }))
                    .sort((a, b) => a.time - b.time);
                
                this.volumeSeries.setData(volumeData);
            }
            
            // Adjust candlestick margins
            this.candlestickSeries.priceScale().applyOptions({
                scaleMargins: { top: 0.1, bottom: 0.3 },
            });
        } else if (!show && this.volumeSeries) {
            // Remove volume series
            this.chart.removeSeries(this.volumeSeries);
            this.volumeSeries = null;
            
            // Reset candlestick margins
            this.candlestickSeries.priceScale().applyOptions({
                scaleMargins: { top: 0.1, bottom: 0.1 },
            });
        }
    }

    /**
     * Change the selected asset - DEPRECATED
     * Terminal-view handles this via child process lifecycle (stopCandleChart + startCandleChart)
     * This method should NOT be used anymore - keeping for backwards compatibility
     */
    async changeAsset(newAsset) {
        // console.warn('⚠️ TradingChart.changeAsset() is deprecated - terminal-view should manage child process lifecycle');
        
        if (newAsset === this.selectedAsset) {
            return;
        }

        // Invalidate any pending extended historical loads (stale request protection)
        this._extendedLoadId++;

        // Just update the label for UI synchronization
        this.selectedAsset = newAsset;
        
        // Clear existing data and overlays
        if (this.candlestickSeries) {
            this.candlestickSeries.setData([]);
        }
        this.clearPositionOverlaysSync();
        this.lastFormattedCandle = null;  // Reset candle tracking
        this.volumeDataCache.clear();  // Clear volume cache for new asset
    }

    /**
     * Change chart interval
     * REPLACED: Uses Charts View advanced loading with better error handling
     */
    async changeInterval(newInterval) {
        if (newInterval === this.interval) return;

        // console.log(`📊 Changing interval from ${this.interval} to ${newInterval}`);
        
        // Invalidate any pending extended historical loads (stale request protection)
        this._extendedLoadId++;
        
        // Clean up old chart instance before recreation
        // Clear price line check interval to avoid duplicates
        if (this.priceLineCheckInterval) {
            clearInterval(this.priceLineCheckInterval);
            this.priceLineCheckInterval = null;
        }
        
        // Remove resize listener if exists
        if (this._boundHandleResize) {
            window.removeEventListener('resize', this._boundHandleResize);
            this._boundHandleResize = null;
        }
        
        // Remove old chart instance cleanly
        if (this.chart) {
            this.chart.remove();
            this.chart = null;
        }
        
        // Show loading state (this clears the container DOM via innerHTML='')
        this.showLoadingState();
        this.interval = newInterval;

        // Reset state flags for new interval
        this.hasInitialData = false;  // Reset flag for new interval
        this.lastFormattedCandle = null;  // Reset candle tracking
        this.volumeDataCache.clear();  // Clear volume cache for new interval
        this.currentPriceLine = null;  // Reset price line reference
        this.candlestickSeries = null;  // Reset series reference
        this.volumeSeries = null;  // Reset volume series reference

        // CRITICAL: Recreate chart widget after cleanup
        // This matches the init() pattern: showLoadingState() → createChart() → loadInitialData()
        await this.createChart();

        // Reload with new interval using advanced loading
        await this.loadInitialData();
        
        // Note: Terminal view handles live updates via candle-live.js child process
        // The child process continues streaming at the new interval
        
        // Hide loading state immediately when data ready
        this.hideLoadingState();
    }

    /**
     * Show loading state with overlay
     */
    showLoadingState(message = 'Loading...') {
        // Remove existing overlay
        this.hideLoadingState();
        
        // Clear skeleton and any existing content
        this.container.innerHTML = '';
        
        // Add loading class to container
        this.container.classList.add('loading');
        
        // Create loading overlay
        const overlay = document.createElement('div');
        overlay.className = 'chart-loading-overlay';
        overlay.innerHTML = `
            <div class="chart-loading-content">
                <div class="chart-loading-spinner"></div>
                <div class="chart-loading-text">${message}</div>
            </div>
        `;
        
        // Set container position and append overlay
        this.container.style.position = 'relative';
        this.container.appendChild(overlay);
    }
    
    /**
     * Hide loading state with smooth transition
     * SSEv4-main pattern: No minimum duration - hide immediately
     */
    hideLoadingState() {
        // Remove loading class from container
        this.container.classList.remove('loading');
        
        const overlay = this.container.querySelector('.chart-loading-overlay');
        if (overlay && overlay.parentNode) {
            overlay.parentNode.removeChild(overlay);
        }
    }

    /**
     * Unsubscribe from real-time updates
     */
    async unsubscribeFromUpdates() {
        if (this.subscription) {
            // Remove event listeners
            window.electronAPI.removeAllListeners('hyperliquid:candleUpdate');
            this.subscription = null;
        }
    }

    /**
     * Handle window resize
     */
    handleResize() {
        if (this.chart && this.container) {
            this.chart.applyOptions({
                width: this.container.clientWidth,
                height: this.container.clientHeight,
            });
        }
    }

    /**
     * Fit chart content to visible range
     */
    fitContent() {
        if (this.chart) {
            this.chart.timeScale().fitContent();
        }
    }

    /**
     * Clear position overlays synchronously
     * SSEv4-main pattern: Simple, blocking clear for predictability
     */
    clearPositionOverlaysSync() {
        if (!this.positionLines || this.positionLines.size === 0) {
            return;
        }
        
        // Remove all lines synchronously
        this.positionLines.forEach((line, key) => {
            if (this.candlestickSeries) {
                try {
                    this.candlestickSeries.removePriceLine(line);
                } catch (error) {
                    // Silently handle errors (line may already be removed)
                }
            }
        });
        
        // Clear the map
        this.positionLines.clear();
    }

    /**
     * Clear position overlays synchronously
     * FIX: Async clearing caused timing issues with rapid recreations
     */
    clearPositionOverlaysAsync() {
        if (!this.positionLines || this.positionLines.size === 0) {
            return;
        }
        
        // Remove all lines synchronously to avoid race conditions
        this.positionLines.forEach((line, key) => {
            if (this.candlestickSeries) {
                try {
                    this.candlestickSeries.removePriceLine(line);
                } catch (error) {
                    // Silently handle errors (line may already be removed)
                }
            }
        });
        
        // Clear the map
        this.positionLines.clear();
    }

    /**
     * Update position and order overlays on chart
     */
    updatePositionOverlays(positions, orders) {
        if (!this.candlestickSeries) return;

        // Store positions/orders for recreation after setData() calls
        this.lastPositions = positions;
        this.lastOrders = orders;

        // OPTIMIZATION: Clear existing position lines asynchronously
        this.clearPositionOverlaysAsync();

        // Add position entry lines
        if (positions) {
            const matchingPositions = positions.filter(pos => pos.coin === this.selectedAsset);
            matchingPositions.forEach(position => {
                const isLong = position.side === 'LONG';
                const entryPrice = parseFloat(position.entryPrice);
                const color = isLong ? '#3b82f6' : '#ef4444'; // Blue for longs, red for shorts

                // For spot pairs, just show pair name. For perps, show side + asset
                const isSpot = position.coin && position.coin.startsWith('@');
                const title = isSpot 
                    ? this.getAssetDisplayName(position.coin)
                    : `${position.side} ${this.getAssetDisplayName(position.coin)}`;
                
                const priceLineOptions = {
                    price: entryPrice,
                    color: color,
                    lineWidth: 2,
                    lineStyle: 1, // Solid line
                    axisLabelVisible: true,
                    title: title
                };

                const priceLine = this.candlestickSeries.createPriceLine(priceLineOptions);
                this.positionLines.set(`pos_${position.coin}`, priceLine);
            });
        }

        // Add open order lines
        if (orders) {
            // Filter for non-TP/SL orders matching current asset
            const matchingOrders = orders.filter(order => {
                const matchesAsset = order.coin === this.selectedAsset;
                const isNotTpsl = !order.isPositionTpsl; // FIX: Truthy check, not strict equality
                // Asset and order matching logic
                return matchesAsset && isNotTpsl;
            });
            
            matchingOrders.forEach(order => {
                const orderPrice = parseFloat(order.limitPx);
                const color = order.side === 'BUY' ? '#3b82f6' : '#ef4444';

                // For spot pairs, just show pair name. For perps, show side + asset
                const isSpot = order.coin && order.coin.startsWith('@');
                const title = isSpot 
                    ? this.getAssetDisplayName(order.coin)
                    : `${order.side} ${this.getAssetDisplayName(order.coin)}`;
                
                const priceLineOptions = {
                    price: orderPrice,
                    color: color,
                    lineWidth: 1.5,
                    lineStyle: 2, // Dashed line
                    axisLabelVisible: true,
                    title: title
                };

                try {
                    const priceLine = this.candlestickSeries.createPriceLine(priceLineOptions);
                    this.positionLines.set(`order_${order.oid}`, priceLine);
                } catch (error) {
                    // Silently handle errors for invalid price lines
                    // console.error('Error creating order price line:', error);
                }
            });

            // Add TP/SL order lines
            const tpslOrders = orders.filter(order => order.coin === this.selectedAsset && order.isPositionTpsl);
            tpslOrders.forEach(order => {
                const orderPrice = parseFloat(order.limitPx);
                
                // Determine if this is a profitable exit (TP) or stop loss
                const matchingPosition = positions?.find(p => p.coin === order.coin);
                if (matchingPosition) {
                    const isLong = matchingPosition.side === 'LONG';
                    const isProfitable = isLong ? 
                        (orderPrice > matchingPosition.entryPrice) : 
                        (orderPrice < matchingPosition.entryPrice);

                    // For spot pairs, just show pair name. For perps, show TP/SL + asset
                    const isSpot = order.coin && order.coin.startsWith('@');
                    const title = isSpot 
                        ? this.getAssetDisplayName(order.coin)
                        : `${isProfitable ? 'TP' : 'SL'} ${this.getAssetDisplayName(order.coin)}`;
                    
                    const priceLineOptions = {
                        price: orderPrice,
                        color: isProfitable ? '#4ade80' : '#f87171',
                        lineWidth: 1.5,
                        lineStyle: 3, // Dotted line
                        axisLabelVisible: true,
                        title: title
                    };

                    const priceLine = this.candlestickSeries.createPriceLine(priceLineOptions);
                    this.positionLines.set(`tpsl_${order.oid}`, priceLine);
                }
            });
        }
    }

    /**
     * Destroy the chart and clean up resources
     */
    destroy() {
        // console.log('[CHART-INSTANCE] 🗑️ DESTROY called on instance:', this._instanceId);
        
        // Remove from global registry
        window._tradingChartInstances?.delete(this);
        
        this.unsubscribeFromUpdates();
        this.hideLoadingState();
        
        // Clear price line check interval
        if (this.priceLineCheckInterval) {
            clearInterval(this.priceLineCheckInterval);
            this.priceLineCheckInterval = null;
        }
        
        // Remove current price line
        if (this.currentPriceLine && this.candlestickSeries) {
            // console.log('[CHART-INSTANCE] 🗑️ Removing price line on instance:', this._instanceId);
            try {
                this.candlestickSeries.removePriceLine(this.currentPriceLine);
            } catch (e) {
                // Silently handle if already removed
            }
            this.currentPriceLine = null;
        }
        
        // OPTIMIZATION: Clear position lines efficiently
        if (this.positionLines && this.positionLines.size > 0) {
            const linesToRemove = Array.from(this.positionLines.values());
            this.positionLines.clear();
            
            if (this.candlestickSeries) {
                for (const line of linesToRemove) {
                    try {
                        this.candlestickSeries.removePriceLine(line);
                    } catch (error) {
                        // Silently handle errors during cleanup
                    }
                }
            }
        }
        
        if (this.chart) {
            if (this._boundHandleResize) {
                window.removeEventListener('resize', this._boundHandleResize);
                this._boundHandleResize = null;
            }
            this.chart.remove();
            this.chart = null;
        }
        
        this.candlestickSeries = null;
        this.lastPrice = 0;
        this.lastFormattedCandle = null;  // Reset candle tracking
        this.volumeDataCache.clear();  // Clear volume cache on destroy
    }

    /**
     * Merge historical data with existing data, deduplicating by timestamp
     * @param {Array} existing - Current chart data
     * @param {Array} newData - New historical data to merge
     * @returns {Array} Merged and sorted data
     */
    mergeHistoricalData(existing, newData) {
        // Use Map for O(n) deduplication by timestamp
        const dataMap = new Map();
        
        // Add new historical data first (lower priority)
        newData.forEach(candle => {
            dataMap.set(candle.time, candle);
        });
        
        // Overlay existing data (higher priority - preserves live updates)
        existing.forEach(candle => {
            dataMap.set(candle.time, candle);
        });
        
        // Convert back to sorted array by timestamp
        return Array.from(dataMap.values())
            .sort((a, b) => a.time - b.time);
    }

    /**
     * Recreate all state after setData() clears overlays
     * Consolidates price line and overlay recreation logic
     */
    recreateStateAfterSetData() {
        // 1. Recreate price line immediately
        if (this.lastPrice > 0) {
            this.updateCurrentPrice(this.lastPrice);
        } else if (window.currentTerminalView?.lastKnownPrice && 
                   window.currentTerminalView?.selectedAsset === this.selectedAsset) {
            this.lastPrice = window.currentTerminalView.lastKnownPrice;
            this.updateCurrentPrice(this.lastPrice);
        }
        
       
        
        // 3. Recreate position/order overlays with small delay for chart stability
        if (this.lastPositions || this.lastOrders) {
            setTimeout(() => {
                this.updatePositionOverlays(this.lastPositions, this.lastOrders);
            }, 100);
        }
        
        // 4. Additional aggressive retries for price line (existing pattern)
        const retryDelays = [50, 150, 300];
        retryDelays.forEach(delay => {
            setTimeout(() => {
                if (this.candlestickSeries && !this.currentPriceLine && this.lastPrice > 0) {
                    this.updateCurrentPrice(this.lastPrice);
                }
            }, delay);
        });
    }

    /**
     * Show subtle loading badge for extended historical data loading
     * Non-blocking corner indicator
     */
    showExtendedLoadingBadge(message = 'Loading history...') {
        this.hideExtendedLoadingBadge(); // Remove existing badge first
        
        const badge = document.createElement('div');
        badge.className = 'chart-extended-loading-badge';
        badge.innerHTML = `
            <div class="badge-spinner"></div>
            <span>${message}</span>
        `;
        this.container.appendChild(badge);
    }

    /**
     * Hide extended loading badge
     */
    hideExtendedLoadingBadge() {
        const badge = this.container.querySelector('.chart-extended-loading-badge');
        if (badge && badge.parentNode) {
            badge.parentNode.removeChild(badge);
        }
    }

    /**
     * Get current chart data
     */
    getData() {
        if (this.candlestickSeries) {
            return this.candlestickSeries.data();
        }
        return [];
    }

    /**
     * Update chart with new candle data from live stream
     * OPTION C: Smart transition - only setData() if no initial data exists
     */
    updateCandles(historicalData, currentCandle) {
        try {
            if (!this.candlestickSeries) {
                // console.warn('❌ No candlestick series available for update');
                return;
            }

            // If we have historical data from child process
            if (historicalData && Array.isArray(historicalData) && historicalData.length > 0) {
                // OPTION C: Only use setData() if we DON'T have initial data yet
                if (!this.hasInitialData) {
                    // console.log('[CHART-CANDLES] updateCandles setData on instance:', this._instanceId, 'priceLine before:', !!this.currentPriceLine);
                    const formattedHistorical = this.formatCandleData(historicalData);
                    
                    // setData() clears all price lines - we need to recreate them after
                    this.candlestickSeries.setData(formattedHistorical);
                    // console.log('[CHART-CANDLES] updateCandles setData complete, priceLine after:', !!this.currentPriceLine);
                    
                    // Show chart widget now that initial data is loaded
                    const chartWidget = this.container.querySelector('.tv-lightweight-charts');
                    if (chartWidget) {
                        chartWidget.style.visibility = 'visible';
                    }
                    
                    this.hasInitialData = true;
                    
                    // Ensure the view shows sufficient history after a bulk update
                    this.fitContent();
                    
                    // FIX: Single delayed overlay recreation
                    if (this.lastPositions || this.lastOrders) {
                        setTimeout(() => {
                            if (this.lastPositions || this.lastOrders) {
                                this.updatePositionOverlays(this.lastPositions, this.lastOrders);
                            }
                        }, 150);
                    }
                    
                    // Recreate price line immediately after setData() clears it
                    const price = this.lastPrice || window.currentTerminalView?.lastKnownPrice;
                    if (price > 0) {
                        this.lastPrice = price;
                        this.updateCurrentPrice(price);
                    }
                    
                    // BULLETPROOF: Aggressive retries for live candle updates
                    const retryDelays = [50, 150, 300];
                    retryDelays.forEach(delay => {
                        setTimeout(() => {
                            if (this.candlestickSeries && !this.currentPriceLine) {
                                const retryPrice = this.lastPrice || window.currentTerminalView?.lastKnownPrice;
                                if (retryPrice > 0) {
                                    this.lastPrice = retryPrice;
                                    this.updateCurrentPrice(retryPrice);
                                }
                            }
                        }, delay);
                    });
                    
                    // Always populate volume cache (even if series doesn't exist yet)
                    if (formattedHistorical.length > 0) {
                        const volumeData = this.extractVolumeData(formattedHistorical);
                        
                        // Populate volume cache
                        volumeData.forEach(vol => {
                            this.volumeDataCache.set(vol.time, { value: vol.value, color: vol.color });
                        });
                        
                        // Set volume data only if series exists
                        if (this.volumeSeries) {
                            this.volumeSeries.setData(volumeData);
                        }
                    }
                } else {
                    // We already have initial data - just update the current candle
                    // console.log('📊 Initial data exists - skipping historicalData setData()');
                }
            }

            // Update with current live candle (incremental update, doesn't clear overlays)
            if (currentCandle) {
                const formattedCandle = this.formatSingleCandle(currentCandle);
                if (formattedCandle) {
                    // console.log('📊 [CHART] Updating candle:', {
                    //     time: new Date(formattedCandle.time * 1000).toISOString(),
                    //     close: formattedCandle.close
                    // });
                    this.candlestickSeries.update(formattedCandle);
                    // Store for live price sync
                    this.lastFormattedCandle = { ...formattedCandle };
                    
                    // Update volume incrementally (doesn't clear existing volume bars)
                    if (this.volumeSeries && formattedCandle.volume > 0) {
                        const isGreen = formattedCandle.close >= formattedCandle.open;
                        const volumeBar = {
                            time: formattedCandle.time,
                            value: formattedCandle.volume,
                            color: isGreen ? 'rgba(166, 227, 161, 0.4)' : 'rgba(243, 139, 168, 0.4)'
                        };
                        
                        // Update cache
                        this.volumeDataCache.set(volumeBar.time, { value: volumeBar.value, color: volumeBar.color });
                        
                        // Incremental update - doesn't clear other bars
                        this.volumeSeries.update(volumeBar);
                    }
                }
            }

        } catch (error) {
            // console.error('❌ Error updating candles:', error);
        }
    }
    
    /**
     * Update current market price line on chart
     * Called from orderbook updates for real-time price display
     * BULLETPROOF VERSION: Handles all edge cases and race conditions
     */
    updateCurrentPrice(price) {
        // console.log('[CHART-PRICE] ========== updateCurrentPrice START ==========');
        // console.log('[CHART-PRICE] Instance ID:', this._instanceId);
        // console.log('[CHART-PRICE] Input price:', price);
        // console.log('[CHART-PRICE] this.lastPrice (before):', this.lastPrice);
        // console.log('[CHART-PRICE] this.candlestickSeries exists:', !!this.candlestickSeries);
        // console.log('[CHART-PRICE] this.currentPriceLine exists:', !!this.currentPriceLine);
        // console.log('[CHART-PRICE] this.currentPriceLine object:', this.currentPriceLine);
        
        if (!price || price <= 0) {
            // console.log('[CHART-PRICE] ❌ RETURNING: Invalid price');
            return;
        }
        
        // Store price immediately for later use
        this.lastPrice = price;
        // console.log('[CHART-PRICE] this.lastPrice (after):', this.lastPrice);
        
        // If chart not ready yet, return (price is stored for later)
        if (!this.candlestickSeries) {
            // console.log('[CHART-PRICE] ❌ RETURNING: No candlestickSeries');
            return;
        }
        
        try {
            // If price line exists, UPDATE it with applyOptions (more reliable than remove/create)
            if (this.currentPriceLine) {
                // console.log('[CHART-PRICE] 🔄 Attempting applyOptions with price:', price);
                // console.log('[CHART-PRICE] Price line options() before:', this.currentPriceLine.options ? this.currentPriceLine.options() : 'no options method');
                this.currentPriceLine.applyOptions({ price: price });
                // console.log('[CHART-PRICE] ✅ applyOptions called successfully');
                // console.log('[CHART-PRICE] Price line options() after:', this.currentPriceLine.options ? this.currentPriceLine.options() : 'no options method');
                
                // Also update current candle bar to sync with price line
                this.updateCurrentCandleWithPrice(price);
                
                // console.log('[CHART-PRICE] ========== updateCurrentPrice END (applyOptions) ==========');
                return;
            }
            
            // console.log('[CHART-PRICE] 🆕 Creating NEW price line at:', price);
            // Create new price line at current market price
            this.currentPriceLine = this.candlestickSeries.createPriceLine({
                price: price,
                color: '#00BF63',           // EVPlus green
                lineWidth: 2,
                lineStyle: 2,               // Dashed (LineStyle.Dashed = 2)
                axisLabelVisible: true,
                title: '',                  // No title - only show price on axis
                lineVisible: true
            });
            // console.log('[CHART-PRICE] ✅ Price line created:', this.currentPriceLine);
            
            // Also update current candle bar to sync with price line
            this.updateCurrentCandleWithPrice(price);
            
            // console.log('[CHART-PRICE] ========== updateCurrentPrice END (created) ==========');
            
        } catch (error) {
            // console.error('[CHART-PRICE] ❌ ERROR:', error);
            // Reset and retry
            this.currentPriceLine = null;
            setTimeout(() => {
                if (this.candlestickSeries && !this.currentPriceLine && this.lastPrice > 0) {
                    try {
                        this.currentPriceLine = this.candlestickSeries.createPriceLine({
                            price: this.lastPrice,
                            color: '#00BF63',
                            lineWidth: 2,
                            lineStyle: 2,
                            axisLabelVisible: true,
                            title: '',
                            lineVisible: true
                        });
                        // console.log('[CHART-PRICE] ✅ Retry price line created');
                    } catch (retryError) {
                        // console.error('[CHART-PRICE] ❌ Retry also failed:', retryError);
                    }
                }
            }, 100);
        }
    }

    /**
     * Update asset label (for UI synchronization)
     */
    updateAssetLabel(asset) {
        this.selectedAsset = asset;
    }
    
    /**
     * Update current candle bar with live price
     * Syncs the current candle's close/high/low with the price line
     */
    updateCurrentCandleWithPrice(price) {
        if (!this.lastFormattedCandle || !this.candlestickSeries || !price || price <= 0) {
            return;
        }
        
        try {
            // Update candle with new price - expand high/low if needed
            const updatedCandle = {
                time: this.lastFormattedCandle.time,
                open: this.lastFormattedCandle.open,
                high: Math.max(this.lastFormattedCandle.high, price),
                low: Math.min(this.lastFormattedCandle.low, price),
                close: price
            };
            
            // Apply update to chart
            this.candlestickSeries.update(updatedCandle);
            
            // Update stored candle with new values
            this.lastFormattedCandle.high = updatedCandle.high;
            this.lastFormattedCandle.low = updatedCandle.low;
            this.lastFormattedCandle.close = price;
        } catch (error) {
            // Silently handle - candle sync is non-critical
        }
    }
    
    /**
     * Static method to update price on ALL chart instances
     * This ensures the visible chart always gets updated regardless of which instance is tracked
     */
    static updateAllInstancesPrice(price, asset) {
        if (!window._tradingChartInstances || !price || price <= 0) return;
        
        for (const chart of window._tradingChartInstances) {
            // Only update charts for the matching asset
            if (chart.selectedAsset === asset && chart.candlestickSeries) {
                chart.updateCurrentPrice(price);
            }
        }
    }
    
    /**
     * Load spot pairs mapping from spotMeta
     * Dynamically fetches ALL spot pairs, not just hardcoded ones
     */
    async loadSpotPairs() {
        if (this.spotPairsLoaded) return;
        
        try {
            if (!window.BrowserHyperliquidClient?.isInitialized) {
                // Client not ready yet, retry later
                setTimeout(() => this.loadSpotPairs(), 1000);
                return;
            }
            
            const spotMeta = await window.BrowserHyperliquidClient.getSpotMeta();
            if (spotMeta?.universe && spotMeta?.tokens) {
                // Build mapping: @index → { name, baseToken, quoteToken }
                for (const pair of spotMeta.universe) {
                    const baseToken = spotMeta.tokens.find(t => t.index === pair.tokens[0]);
                    const quoteToken = spotMeta.tokens.find(t => t.index === pair.tokens[1]);
                    
                    if (baseToken && quoteToken) {
                        this.spotPairs[`@${pair.index}`] = {
                            name: pair.name || `${baseToken.name}/${quoteToken.name}`,
                            baseToken: baseToken.name,
                            quoteToken: quoteToken.name
                        };
                    }
                }
                this.spotPairsLoaded = true;
                // console.log(`[CHART] Loaded ${Object.keys(this.spotPairs).length} spot pairs`);
            }
        } catch (error) {
            // Silently fail - will retry or use fallback
            // console.warn('[CHART] Failed to load spot pairs:', error.message);
        }
    }
    
    /**
     * Get display name for asset (spot pairs show as USDH/USDC, others show as-is)
     * Matches terminal-view.js formatAssetWithDexBadge() pattern
     */
    getAssetDisplayName(asset) {
        if (asset && asset.startsWith('@')) {
            // Check dynamic spot pairs first
            if (this.spotPairs[asset]) {
                return this.spotPairs[asset].name;
            }
            // Fallback: return as-is if not found
            return asset;
        }
        return asset;
    }

    /**
     * Check if chart is ready
     */
    isReady() {
        return this.chart !== null && this.candlestickSeries !== null;
    }
}

// Export for global access
window.TradingChart = TradingChart;
